import type { ReactNode } from "react";
import { Box, Group, Skeleton, Stack, Table, VisuallyHidden } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";

/** Reserve the dynamic slot, leaving the page's headings, help and controls mounted. */
export function LoadingContent({ label, variant = "text", rows = 3, height: slotHeight, pending = true }: {
  label: string;
  variant?: "text" | "rows" | "cards" | "editor";
  rows?: number;
  height?: number;
  pending?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const height = slotHeight ?? (variant === "editor" ? 180 : variant === "cards" ? 148 : variant === "rows" ? 64 : 14);
  return <Box aria-busy={pending} w="100%">
    {pending ? <VisuallyHidden role="status">{label}</VisuallyHidden> : null}
    <Stack gap={variant === "text" ? "sm" : "md"} aria-hidden="true">
      {variant === "editor" ? <Skeleton height={42} animate={pending && !reducedMotion} /> : null}
      {Array.from({ length: variant === "editor" ? 1 : rows }, (_, index) => <Skeleton key={index} height={height} width={variant === "text" && index === rows - 1 ? "70%" : "100%"} animate={pending && !reducedMotion} />)}
    </Stack>
  </Box>;
}

/** Mask an unknown value only; its static label stays readable. */
export function LoadingValue({ loading, children, width = 120, pending = true }: { loading: boolean; children: ReactNode; width?: number; pending?: boolean }) {
  const reducedMotion = useReducedMotion();
  return loading ? <Skeleton component="span" display="inline-block" height="1em" width={width} animate={pending && !reducedMotion} aria-hidden="true" /> : children;
}

export function LoadingField({ label, description, height: slotHeight, pending = true, kind = "input", labelPosition = "right" }: {
  label: string; description?: string; height?: number; pending?: boolean;
  kind?: "input" | "switch" | "checkbox"; labelPosition?: "left" | "right";
}) {
  const reducedMotion = useReducedMotion();
  const height = slotHeight ?? (kind === "input" ? 36 : kind === "checkbox" ? 18 : 24);
  const caption = <Box flex={kind === "input" ? undefined : 1}><Box component="span" fz="sm" fw={500}>{label}</Box>{description ? <Box fz="xs" c="dimmed">{description}</Box> : null}</Box>;
  const placeholder = <Skeleton height={height} width={kind === "input" ? "100%" : kind === "checkbox" ? 18 : 46} style={{ flexShrink: 0 }} animate={pending && !reducedMotion} aria-hidden="true" />;
  return kind === "input" ? <Stack gap={4}>{caption}{placeholder}</Stack> : <Group wrap="nowrap" gap="md">{labelPosition === "left" ? caption : placeholder}{labelPosition === "left" ? placeholder : caption}</Group>;
}

export function LoadingTableRows({ columns, label, rows = 3 }: { columns: number; label: string; rows?: number }) {
  const reducedMotion = useReducedMotion();
  return Array.from({ length: rows }, (_, row) => <Table.Tr key={row}>
    {Array.from({ length: columns }, (_, column) => <Table.Td key={column}>{row === 0 && column === 0 ? <VisuallyHidden role="status">{label}</VisuallyHidden> : null}<Skeleton height={24} animate={!reducedMotion} aria-hidden="true" /></Table.Td>)}
  </Table.Tr>);
}
